<template>
  <div>
    <div class="page-header batch-create-page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4 class="mb-0">{{ $t('new_batch') }}</h4>
        </div>
      </div>
      <ul class="table-top-head batch-create-header-actions">
        <li class="batch-header-invoice-qty">
          <div class="batch-line-stock-bar">
            <span class="batch-line-invoice-qty-label">{{ $t('current_invoice_quantity') }}</span>
            <span class="batch-line-invoice-qty-value">{{ currentInvoiceQuantity }}</span>
          </div>
        </li>
        <li>
          <div class="page-btn">
            <router-link to="/batches" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
      </ul>
    </div>

    <div class="card table-list-card batch-create-card bg-white fs-7">
      <form @submit.prevent="submitBatch" enctype="multipart/form-data">
        <div class="row g-2">
          <!-- Receiving No -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("receiving_no") }}
            </label>
            <input
              type="text"
              class="form-control"
              v-model="batches.receiving_no"
              readonly
            />
          </div>

          <!-- Receiving Date -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("receiving_date") }}
              <span class="text-danger">*</span>
            </label>
            <input
              type="date"
              class="form-control"
              v-model="batches.receiving_date"
              @change="fetchReceivingNo"
            />
            <small v-if="errors.receiving_date" class="text-danger">
              {{ errors.receiving_date }}
            </small>
          </div>

          <!-- Warehouse -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("warehouse_name") }}
              <span class="text-danger">*</span>
            </label>

            <FormDropdown
              v-model="batches.warehouse_id"
              :options="formattedwarehouses"
              clearable
              @select="(value) => handleSelect2Change('warehouse_id', value)"
              class="flex-grow-1"
            />

            <div class="error-slot">
              <span v-if="errors.warehouse_id" class="text-danger fw-medium">
                {{ errors.warehouse_id }}
              </span>
            </div>
          </div>

          <!-- Chalan No -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("chalan_no") }}
              <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="batches.chalan_no"
              :placeholder="$t('enter_chalan_no')"
              @input="clearError('chalan_no')"
            />
            <div class="error-slot">
              <span v-if="errors.chalan_no" class="text-danger fw-medium">
                {{ errors.chalan_no }}
              </span>
            </div>
          </div>

          <!-- Chalan Date -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("chalan_date") }}
              <span class="text-danger">*</span>
            </label>
            <input
              type="date"
              class="form-control"
              v-model="batches.chalan_date"
            />
            <small v-if="errors.chalan_date" class="text-danger">
              {{ errors.chalan_date }}
            </small>
          </div>

          <!-- In House Date -->
          <div class="col-lg-3 col-md-6">
            <label class="form-label">
              {{ $t("in_house_date") }}
              <span class="text-danger">*</span>
            </label>

            <input
              type="date"
              class="form-control"
              v-model="batches.in_house_date"
            />

            <small v-if="errors.in_house_date" class="text-danger">
              {{ errors.in_house_date }}
            </small>
          </div>

          <!-- Supplier -->
          <div class="col-lg-3 col-md-6">
            <div class="add-newplus">
              <label class="form-label">
                {{ $t("supplier_name") }}
                <span class="text-danger">*</span>
              </label>
              <a
                v-if="canCreateSupplier"
                href="javascript:void(0);"
                data-bs-toggle="modal"
                data-bs-target="#create-modal7"
              >
                <i class="ti ti-circle-plus me-1"></i><span>{{ $t("add_new") }}</span>
              </a>
            </div>

            <FormDropdown
              v-model="batches.supplier_id"
              :options="formattedsuppliers"
              clearable
              @update:modelValue="selectSupplier"
              @select="(value) => handleSelect2Change('supplier_id', value)"
              class="flex-grow-1"
            />

            <div class="error-slot">
              <span v-if="errors.supplier_id" class="text-danger fw-medium">
                {{ errors.supplier_id }}
              </span>
            </div>
          </div>

          <!-- Supplier Info (fills blank column beside Supplier DD; slot always reserved) -->
          <div class="col-lg-3 col-md-6">
            <div
              class="supplier-info-panel"
              :class="{ 'is-empty': !selectedSupplier }"
              :title="selectedSupplier ? supplierInfoTitle : ''"
            >
              <template v-if="selectedSupplier">
                <div class="supplier-info-main">
                  <span class="supplier-info-name">{{ selectedSupplier.name }}</span>
                  <span class="supplier-info-sep" aria-hidden="true">·</span>
                  <span class="supplier-info-phone">{{ selectedSupplier.phone_number || '—' }}</span>
                </div>
                <span
                  class="supplier-info-due"
                  :class="Number(selectedSupplier.due_balance) > 0 ? 'is-due' : 'is-clear'"
                >
                  Due ৳{{ selectedSupplier.due_balance || 0 }}
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- Item line entry: search + fields + Add (single row) -->
        <div class="batch-line-entry mb-2">
          <div class="batch-line-fields">
            <div
              class="batch-line-field batch-line-search"
              :class="{ 'batch-line-search-wide': isSelectedWarranty && selectedVariantLabel }"
            >
              <label class="form-label">
                {{ $t('search_item_by_name_code') }}
                <span class="text-danger">*</span>
              </label>
              <div class="position-relative">
                <input
                  ref="itemSearch"
                  type="text"
                  autocomplete="off"
                  v-model="query"
                  @input="onSearchInput"
                  :placeholder="$t('search_item_by_name_code')"
                  class="form-control form-control-sm"
                  :disabled="warrantyEntryLocked"
                  @keydown="handleKeys"
                />

                <div
                  v-if="results.length || selectedParentItem"
                  class="search-results shadow rounded bg-white position-absolute mt-1 w-100"
                  style="max-height: 450px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0; min-width: 320px;"
                >
                  <div
                    v-if="selectedParentItem"
                    class="d-flex justify-content-end align-items-center p-2 border-bottom bg-light"
                  >
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click.stop="openVariantModal"
                    >
                      <i class="ti ti-plus me-1"></i>
                      {{ $t('add_new_variant') }}
                    </button>
                  </div>

                  <ul class="list-group list-group-flush">
                    <li
                      v-for="(item, index) in results"
                      :key="item.id"
                      class="list-group-item d-flex justify-content-between align-items-center"
                      :class="{ 'bg-primary text-white': index === itemIndex }"
                      style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    >
                      <div
                        class="flex-grow-1 d-flex flex-column"
                        @click="selectItem(item)"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div
                            style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                          >
                            <img
                              :src="$getRowImageUrl(item)"
                              style="width:100%; height:100%; object-fit:cover;"
                            />
                          </div>
                          <div class="d-flex flex-column">
                            <strong class="text-dark">
                              {{ item.item_name }} - {{ item.item_code }}
                            </strong>
                            <span style="font-size: 0.75rem; color: black;">
                              Stock: {{ getAvailableStock(item) }}
                            </span>
                            <span style="font-size: 0.75rem; color: black;">
                              {{ $t('selling_price') }}: {{ formatMoney(item.selling_price ?? 0) }}
                            </span>
                            <template v-if="showsVariantDetails(item)">
                              <span v-if="item.sku" style="font-size: 0.75rem; color: black;">
                                SKU: {{ item.sku }}
                              </span>
                              <span v-if="item.barcode_qr_code" style="font-size: 0.75rem; color: black;">
                                Barcode: {{ item.barcode_qr_code }}
                              </span>
                              <span
                                v-if="item.variant_attributes?.length"
                                style="font-size: 0.75rem; color: black;"
                              >
                                {{
                                  item.variant_attributes
                                    .map(v => `${v.attribute}: ${v.value}`)
                                    .join(' | ')
                                }}
                              </span>
                            </template>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div v-if="!results.length" class="text-center text-muted py-4">
                    <template v-if="selectedParentItem">
                      No variants yet. Use <strong>Add new variant</strong> above.
                    </template>
                    <template v-else>
                      No results found
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="batch-line-field">
              <label class="form-label">{{ $t('batch_no') }}</label>
              <input
                type="text"
                v-model="batches.batch_no"
                class="form-control form-control-sm"
                :placeholder="$t('enter_batch_no')"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div class="batch-line-field">
              <label class="form-label">{{ $t('lot_no') }}</label>
              <input
                type="text"
                v-model="batches.lot_no"
                class="form-control form-control-sm"
                :placeholder="$t('enter_lot_no')"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div class="batch-line-field batch-line-date">
              <label class="form-label">{{ $t('manufacturing_date') }}</label>
              <input
                type="date"
                v-model="batches.manufacturing_date"
                class="form-control form-control-sm"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div class="batch-line-field batch-line-date">
              <label class="form-label">{{ $t('expiry_date') }}</label>
              <input
                type="date"
                v-model="batches.expiry_date"
                class="form-control form-control-sm"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div v-if="!isSelectedWarranty" class="batch-line-field batch-line-qty">
              <label class="form-label">
                {{ $t('quantity') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                v-model.number="batches.quantity"
                class="form-control form-control-sm"
              />
            </div>

            <div class="batch-line-field">
              <label class="form-label">
                {{ $t('purchase_price') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                v-model.number="batches.costing_unit_price"
                class="form-control form-control-sm"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div class="batch-line-field">
              <label class="form-label">{{ $t('batch_selling_price') }}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                v-model.number="batches.batch_selling_price"
                class="form-control form-control-sm"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div v-if="isSelectedWarranty" class="batch-line-field batch-line-warranty">
              <label class="form-label">
                {{ $t('warranty_period_days') }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                v-model.number="batches.warranty_period_days"
                class="form-control form-control-sm"
                :placeholder="$t('days')"
                :disabled="warrantyEntryLocked"
              />
            </div>

            <div v-if="isSelectedWarranty" class="batch-line-field batch-line-serials">
              <label class="form-label">
                {{ $t('serial_number') }}
                <span class="text-danger">*</span>
              </label>
              <div class="batch-line-serials-row">
                <input
                  ref="serialInput"
                  type="text"
                  v-model="batches.serial_numbers_text"
                  class="form-control form-control-sm"
                  :placeholder="$t('serial_scan_enter_hint')"
                  @keydown.enter.prevent="addWarrantySerial"
                />
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm batch-line-reset"
                  @click="resetWarrantyLine"
                >
                  {{ $t('reset') }}
                </button>
              </div>
            </div>

            <div v-if="!isSelectedWarranty" class="batch-line-field batch-line-add">
              <label class="form-label">&nbsp;</label>
              <button type="button" class="btn btn-success btn-sm w-100" @click="addItem">
                {{ $t('add') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Items grid (Excel-like compact summary) -->
        <div class="table-responsive mb-3 fixed-table excel-grid-wrap">
          <table class="excel-grid">
            <!-- col widths: action locked at 25px so leftover % space cannot expand it -->
            <colgroup>
              <col class="excel-col-hash">
              <col class="excel-col-item">
              <col class="excel-col-variant">
              <col class="excel-col-stock">
              <col class="excel-col-batch">
              <col class="excel-col-lot">
              <col class="excel-col-date">
              <col class="excel-col-date">
              <col v-if="!hasWarrantyItems" class="excel-col-qty">
              <col class="excel-col-money">
              <col class="excel-col-money">
              <col v-if="hasWarrantyItems" class="excel-col-warranty">
              <col v-if="hasWarrantyItems" class="excel-col-serial">
              <col class="excel-col-action">
            </colgroup>
            <thead>
              <tr>
                <th class="col-hash">#</th>
                <th class="col-item">{{ $t("item_name") }}</th>
                <th class="col-variant">{{ $t("variant") }}</th>
                <th class="col-stock">{{ $t("current_stock") }}</th>
                <th class="col-batch">{{ $t("batch_no") }}</th>
                <th class="col-lot">{{ $t("lot_no") }}</th>
                <th class="col-date">{{ $t("manufacturing_date") }}</th>
                <th class="col-date">{{ $t("expiry_date") }}</th>
                <th v-if="!hasWarrantyItems" class="col-qty">{{ $t("quantity") }}</th>
                <th class="col-money">{{ $t("purchase_price") }}</th>
                <th class="col-money">{{ $t("batch_selling_price") }}</th>
                <th v-if="hasWarrantyItems" class="col-warranty">{{ $t('warranty_period_days') }}</th>
                <th v-if="hasWarrantyItems" class="col-serial">{{ $t('serial_numbers') }}</th>
                <th class="col-action" :title="$t('action')">{{ $t("action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in addedItems" :key="index">
                <td class="col-hash num">{{ index + 1 }}</td>
                <td class="col-item" :title="item.item_name">{{ item.item_name }}</td>
                <td class="col-variant">
                  <span v-if="item.variant_attributes?.length" :title="formatVariantAttrs(item)">
                    {{ formatVariantAttrs(item) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="col-stock num">{{ item.current_stock ?? 0 }}</td>
                <td class="col-batch">{{ item.batch_no || '-' }}</td>
                <td class="col-lot">{{ item.lot_no || '-' }}</td>
                <td class="col-date">{{ item.manufacturing_date || '-' }}</td>
                <td class="col-date">{{ item.expiry_date || '-' }}</td>
                <td v-if="!hasWarrantyItems" class="col-qty num">{{ item.quantity }}</td>
                <td class="col-money num">{{ formatMoney(item.costing_unit_price ?? 0) }}</td>
                <td class="col-money num">{{ formatMoney(item.batch_selling_price ?? 0) }}</td>
                <td v-if="hasWarrantyItems" class="col-warranty num">
                  {{ item.item_type_code === 'WARRANTY' ? (item.warranty_period_days || '-') : '-' }}
                </td>
                <td
                  v-if="hasWarrantyItems"
                  class="col-serial"
                  :title="warrantySerialsDisplay(item)"
                >
                  <template v-if="item.item_type_code === 'WARRANTY'">
                    <div class="warranty-serial-cell">
                      <div class="warranty-serial-list">
                        <div
                          v-for="(serial, sIdx) in parseSerialNumbers(item.serial_numbers_text)"
                          :key="`${index}-${sIdx}-${serial}`"
                          class="warranty-serial-row"
                        >
                          <span class="warranty-serial-text">{{ serial }}</span>
                          <button
                            type="button"
                            class="warranty-serial-cancel"
                            :title="$t('cancel')"
                            @click.stop="removeWarrantySerial(index, sIdx)"
                          >
                            ×
                          </button>
                        </div>
                        <span v-if="!parseSerialNumbers(item.serial_numbers_text).length">-</span>
                      </div>
                      <span class="warranty-serial-count">{{ item.quantity || 0 }}</span>
                    </div>
                  </template>
                  <span v-else>-</span>
                </td>
                <td class="col-action">
                  <button
                    type="button"
                    class="excel-grid-del"
                    :title="$t('action')"
                    @click="removeItem(index)"
                  >
                    ×
                  </button>
                </td>
              </tr>
              <tr v-if="!addedItems.length" class="excel-grid-empty">
                <td :colspan="gridColCount">&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment Type -->
        <div class="batch-create-footer mt-4">
          <div class="batch-create-footer-row">
            <div class="batch-create-ref">
              <label class="form-label">{{$t('reference_no')}} </label>
              <input
                type="text"
                v-model="reference_no"
                class="form-control"
                :placeholder= "$t('enter_reference_no')"
              />
              <div v-if="errors.reference_no" class="text-danger">{{ errors.reference_no }}</div>
            </div>

            <div class="batch-create-total">
              <label class="form-label mb-0">{{$t('total')}}</label>
              <input type="text" class="form-control text-end" :value="totalAmount" disabled />
            </div>
          </div>
        </div>

        <div class="batch-create-actions mt-3">
          <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
          <button type="submit" class="btn btn-submit" :disabled="saving">{{ $t('submit') }}</button>
        </div>
      </form>
    </div>

    <!-- Due Confirmation Modal -->
    <div v-if="showDueModal" class="confirm-backdrop" click.self="cancelDueSale">
      <div class="confirm-modal">

        <div class="confirm-header">
          <h4>Due Purchase Confirmation</h4>
        </div>

        <div class="confirm-body">
          <div class="confirm-row">
            <span>Total Amount</span>
            <strong>৳ {{ totalAmount }}</strong>
          </div>

          <div class="confirm-row">
            <span>Paid Amount</span>
            <strong>৳ {{ paid }}</strong>
          </div>

          <div class="confirm-row due">
            <span>Due Amount</span>
            <strong>৳ {{ dueAmount }}</strong>
          </div>
        </div>

        <div class="confirm-actions">
          <button type="button" class="btn btn-secondary" @click="cancelDueSale">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="confirmDueSale">
            Confirm Purchase
          </button>
        </div>
      </div>
    </div>

    <!-- Multi Variant Create Modal -->
    <div
      v-if="showVariantModal"
      class="confirm-backdrop"
      @click.self="closeVariantModal"
    >
      <div
        class="confirm-modal bg-white rounded-4 shadow-lg d-flex flex-column"
        style="
          width:95vw;
          max-width:1500px;
          height:90vh;
          overflow:hidden;
        "
      >
      <!-- ================= HEADER ================= -->
      <div
        class="border-bottom bg-white px-4 py-3"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="d-flex align-items-center gap-3">
              <div
                class="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center"
                style="
                  width:54px;
                  height:54px;
                  font-size:22px;
                "
              >
                📦
              </div>

              <div>
                <h3 class="fw-bold mb-1">
                  Create Item Variants
                </h3>

                <div class="text-muted">
                  Generate item variants from attribute combinations.
                </div>
              </div>
            </div>
          </div>

          <button
            class="btn btn-light rounded-circle"
            style="width:42px;height:42px;"
            @click="closeVariantModal"
          >
            ✕
          </button>
        </div>

        <!-- Parent Item -->
        <div
          v-if="selectedParentItem"
          class="mt-4"
        >
          <div class="row">
            <div class="col-lg-4">
              <div class="border rounded-3 p-3 h-100">
                <div class="text-muted small">
                  Parent Item
                </div>

                <div class="fw-bold fs-4">
                  {{ selectedParentItem.item_name }}
                </div>

                <div class="text-secondary">
                  Code: {{ selectedParentItem.item_code }}
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="border rounded-3 p-3 h-100">
                <div class="text-muted small">
                  Item Type
                </div>

                <div class="fw-bold fs-4">
                  {{ selectedParentItem.item_type_code }}
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="border rounded-3 p-3 h-100">
                <div class="text-muted small">
                  Current Stock
                </div>

                <div class="fw-bold fs-4">
                  {{ selectedParentItem.stock_quantity }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= BODY ================= -->
      <div
        class="flex-grow-1 overflow-auto p-4"
      >

        <!-- ================= Attribute Selection ================= -->
        <div class="row g-3 mb-4">

          <!-- Existing -->
          <div class="col-lg-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <div
                  class="rounded-circle bg-primary bg-opacity-10 mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style="width:60px;height:60px;font-size:26px;"
                >
                  📦
                </div>

                <div class="text-muted small">
                  Existing Variants
                </div>

                <h2 class="fw-bold text-primary mb-0">
                  {{ existingVariants.length }}
                </h2>
              </div>
            </div>
          </div>

          <!-- New -->
          <div class="col-lg-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <div
                  class="rounded-circle bg-success bg-opacity-10 mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style="width:60px;height:60px;font-size:26px;"
                >
                  ✨
                </div>

                <div class="text-muted small">
                  New Variants
                </div>

                <h2 class="fw-bold text-success mb-0">
                  {{ generatedVariants.new.length }}
                </h2>
              </div>
            </div>
          </div>

          <!-- Total -->
          <div class="col-lg-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <div
                  class="rounded-circle bg-warning bg-opacity-10 mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style="width:60px;height:60px;font-size:26px;"
                >
                  📊
                </div>

                <div class="text-muted small">
                  Total After Save
                </div>

                <h2 class="fw-bold text-dark mb-0">
                  {{ existingVariants.length + generatedVariants.new.length }}
                </h2>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div
            class="card-header bg-white border-bottom d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 class="fw-bold mb-0">
                Attribute Selection
              </h5>

              <small class="text-muted">
                Select attribute values to generate variant combinations.
              </small>
            </div>

            <span class="badge bg-primary">
              {{ variant_groups.length }}

              Group<span v-if="variant_groups.length !== 1">s</span>
            </span>
          </div>

          <div class="card-body">
            <div class="row g-4 align-items-end">

              <!-- Attribute -->
              <div class="col-lg-4">
                <div class="add-newplus">
                  <label class="form-label fw-semibold">
                    Attribute
                  </label>
                  <a href="javascript:void(0);" @click="showAttributeModal('create-modal8')">
                    <i class="ti ti-circle-plus me-1"></i>
                    <span>{{ $t("add_new") }}</span>
                  </a>
                </div>

                <FormDropdown
                  v-model="batches.attribute"
                  :options="formattedAttributes"
                  placeholder="Select Attribute"
                  clearable
                  @select="handleAttributeSelect"
                />
              </div>

              <!-- Values -->
              <div class="col-lg-5">
                <div class="add-newplus">
                  <label class="form-label fw-semibold">
                    Attribute Values
                  </label>
                  <a href="javascript:void(0);" @click="openAttributeValueModal">
                    <i class="ti ti-circle-plus me-1"></i>
                    <span>{{ $t("add_new") }}</span>
                  </a>
                </div>

                <FormDropdown
                  v-model="batches.attribute_values"
                  :options="formattedAttributeValues"
                  placeholder="Select Attribute Values"
                  multiple
                />
              </div>

              <!-- Button -->
              <div class="col-lg-3">
                <button
                  class="btn btn-primary w-100"
                  type="button"
                  :disabled="
                    !batches.attribute ||
                    batches.attribute_values.length===0
                  "
                  @click="addVariantGroup"
                >

                  + Add Attribute Group
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ================= Attribute Groups ================= -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h5 class="fw-bold mb-0">
                Selected Attribute Groups
              </h5>

              <small class="text-muted">
                These groups will be used to generate all possible combinations.
              </small>
            </div>

            <span class="badge bg-secondary">
              {{ variant_groups.length }}

              Group<span v-if="variant_groups.length !== 1">s</span>
            </span>
          </div>

          <div class="card-body">
            <!-- Empty State -->
            <div
              v-if="!variant_groups.length"
              class="text-center py-5"
            >

              <div
                class="rounded-circle bg-light mx-auto mb-3 d-flex justify-content-center align-items-center"
                style="width:70px;height:70px;font-size:28px;"
              >
                🧩
              </div>

              <h5 class="fw-semibold">
                No Attribute Groups
              </h5>

              <div class="text-muted">
                Select an attribute and values, then click
                <strong>Add Attribute Group</strong>.
              </div>
            </div>

            <!-- Groups -->
            <div v-else class="row g-3">
              <div class="col-xl-6" v-for="(g,index) in variant_groups" :key="index">
                <div class="border rounded-4 p-4 h-100 shadow-sm">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <div class="fw-bold fs-5">
                        {{ g.attribute_name }}
                      </div>

                      <small class="text-muted">
                        {{ g.values.length }}

                        Value<span v-if="g.values.length!==1">s</span>
                      </small>
                    </div>

                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="removeVariantGroup(index)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>

                  <hr>
                  <div>
                    <span
                      v-for="value in g.values"
                      :key="value.id"
                      class="badge rounded-pill bg-primary me-2 mb-2 px-3 py-2"
                    >

                      {{ value.value }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="card-footer bg-white border-top text-end"
          >

            <button
              class="btn btn-success px-4"
              type="button"
              :disabled="variant_groups.length===0"
              @click="generateVariants"
            >

              <i class="fas fa-cogs me-2"></i>

              Generate Variants
            </button>
          </div>
        </div>

        <!-- ================= Generated Variants ================= -->
        <!-- New Variants -->
        <div
          id="generatedVariants"
          class="card border-0 shadow-sm"
        >

          <div
            class="card-header bg-white border-bottom d-flex justify-content-between align-items-center"
          >

            <div>
              <h5 class="fw-bold mb-0">
                New Variants
              </h5>

              <small class="text-muted">
                Only new combinations appear here.
              </small>
            </div>

            <span
              class="badge bg-success"
            >
              {{ generatedVariants.new.length }}

              New
            </span>
          </div>

          <div class="card-body p-0">
            <!-- Empty -->
            <div
              v-if="!generatedVariants.new.length"
              class="text-center py-5"
            >

              <div
                class="rounded-circle bg-success bg-opacity-10 mx-auto mb-3 d-flex align-items-center justify-content-center"
                style="width:80px;height:80px;font-size:34px;"
              >
                ✔
              </div>

              <h5 class="fw-bold">
                No New Variants
              </h5>

              <div class="text-muted">
                All possible combinations already exist,
                or click Generate Variants after selecting attribute groups.
              </div>
            </div>

            <!-- Table -->
            <div
              v-else
              class="table-responsive"
            >

              <table
                class="table table-hover align-middle mb-0"
              >

                <thead class="table-light">
                  <tr>
                    <th width="260">
                      Variant
                    </th>

                    <th>
                      Barcode
                    </th>

                    <th>
                      SKU
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(variant,index) in generatedVariants.new"
                    :key="'new-'+index"
                  >

                    <td>
                      <span
                        v-for="(a,i) in variant.attributes"
                        :key="i"
                        class="badge bg-primary me-1 mb-1"
                      >

                        {{ a.attribute }}
                        :
                        {{ a.value }}
                      </span>
                    </td>

                    <td>
                      {{ variant.barcode }}
                    </td>

                    <td>
                      {{ variant.sku }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Existing Variants -->
        <div
          v-if="existingVariants.length"
          class="card border-0 shadow-sm mb-4"
        >

          <div
            class="card-header bg-white border-bottom d-flex justify-content-between align-items-center"
          >

            <div>
              <h5 class="fw-bold mb-0">
                Existing Variants
              </h5>

              <small class="text-muted">
                These variants already exist and cannot be generated again.
              </small>
            </div>

            <span class="badge bg-secondary">
              {{ existingVariants.length }}
            </span>
          </div>

          <div class="card-body">
            <div class="row g-3">
              <div
                v-for="(variant,index) in existingVariants"
                :key="'existing-'+index"
                class="col-lg-3 col-md-6"
              >

                <div
                  class="border rounded-4 p-3 h-100 bg-light"
                >

                  <div class="d-flex align-items-center gap-2 mb-2">
                    <img
                      :src="$getRowImageUrl(variant)"
                      alt="Variant"
                      class="rounded flex-shrink-0"
                      style="width:40px; height:40px; object-fit:cover;"
                    />
                    <span class="fw-semibold">{{ variant.sku }}</span>
                  </div>

                  <div class="mb-2">
                    <span
                      v-for="(a,i) in variant.variant_attributes"
                      :key="i"
                      class="badge bg-secondary me-1 mb-1"
                    >

                      {{ a.attribute }}
                      :
                      {{ a.value }}
                    </span>
                  </div>

                  <div
                    class="small text-muted"
                  >
                    Barcode :
                    {{ variant.barcode_qr_code }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="border-top bg-light px-4 py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">

          <!-- Left Summary -->
          <div class="d-flex align-items-center gap-4">
            <div>
              <small class="text-muted d-block">
                Existing Variants
              </small>

              <h5 class="mb-0 fw-bold">
                {{ existingVariants.length }}
              </h5>
            </div>

            <div>
              <small class="text-muted d-block">
                New Variants
              </small>

              <h5
                class="mb-0 fw-bold"
                :class="generatedVariants.new.length? 'text-success' : 'text-muted'"
              >
                {{ generatedVariants.new.length }}
              </h5>
            </div>

            <div>
              <small class="text-muted d-block">
                Total Variants
              </small>

              <h5 class="mb-0 fw-bold">
                {{ existingVariants.length + generatedVariants.new.length }}
              </h5>
            </div>
          </div>

          <!-- Right Buttons -->
          <div class="d-flex gap-2">

            <button
              type="button"
              class="btn btn-light px-4"
              @click="closeVariantModal"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-primary px-5"
              :disabled="!generatedVariants.new.length || savingVariants"
              @click="saveVariant"
            >
              <span v-if="savingVariants" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-save me-2"></i>

              Save {{ generatedVariants.new.length }}
              {{ generatedVariants.new.length === 1 ? 'variant' : 'variants' }}
            </button>

          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Variant save success alert (OK closes modal and focuses search) -->
    <div v-if="showVariantSuccessAlert" class="confirm-backdrop variant-success-alert">
      <div class="confirm-modal">
        <div class="confirm-header">
          <h4>{{ $t('success') || 'Success' }}</h4>
        </div>
        <div class="confirm-body">
          <p class="mb-0" v-html="$t('variants_added_to_item_alert')"></p>
        </div>
        <div class="confirm-actions">
          <button type="button" class="btn btn-primary" @click="acknowledgeVariantSuccess">
            OK
          </button>
        </div>
      </div>
    </div>

  <CreateSupplierModal
    v-if="canCreateSupplier"
    id="create-modal7"
    @create_success="onSupplierCreated"
  />
  <CreateAttributeModal class="over-variant-modal" @create_success="onAttributeCreated" />
  <CreateAttributeValueModal
    class="over-variant-modal"
    :attribute-id="batches.attribute"
    @create_success="onAttributeValueCreated"
  />
</div>
</template>

<script>
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce'
import { truncateMoney2, todayLocal } from '@/utils';
import { apiErrorMessage } from '@/helpers/apiError';
import { can } from '@/helpers/permission';
import CreateSupplierModal from "@/views/admin_dashboard/item/CreateSupplierModal.vue";
import CreateAttributeModal from "@/views/admin_dashboard/item/CreateAttributeModal.vue";
import CreateAttributeValueModal from "@/views/admin_dashboard/item/CreateAttributeValueModal.vue";

export default {
  components: {
    Vue3Datepicker,
    CreateSupplierModal,
    CreateAttributeModal,
    CreateAttributeValueModal,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      batches: {
        item: null, chalan_no: '', chalan_date: todayLocal(), supplier_id: null, batch_no: '', lot_no: '', costing_unit_price: '',
        batch_selling_price: '', quantity: null, in_house_date: todayLocal(), manufacturing_date: null, expiry_date: null,
        warehouse_id: null, receiving_no: '', discount: 0, selling_price: 0, receiving_date: todayLocal(),
        variant_id: null, attribute: null, attribute_values: [], variants: [],
        warranty_period_days: null, serial_numbers_text: '',
      },
      items: [],
      warehouses: [],
      suppliers: [],
      cash_types: [],
      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      warrantyEntryLocked: false,
      activeWarrantyLineIndex: -1,
      addedItems: [],
      bank_accounts: [],
      paid: 0,
      reference_no: '',
      selectedSupplier: null,
      due_balance: 0,
      cash_type: null,
      bank_account: null,
      showDueModal: false,
      showVariantModal: false,
      showVariantSuccessAlert: false,
      savingVariants: false,
      dueModalResolver: null,
      debouncedSearch: null,
      attributes: [],
      selected_attribute_values: [],
      variants: [],
      variant_groups: [],
      selectedParentItem: null,
      existingVariants: [],
      generatedVariants: {
            existing: [],
            new: []
        },
    };
  },

  mounted() {
    this.fetchDropdownData();
  },

  computed: {
    formattedItems() {
      return this.items.map(item => ({ id: item.id, text: item.item_name }));
    },

    formattedwarehouses() {
      return this.warehouses.map(warehouse => ({ id: warehouse.id, text: warehouse.name }));
    },

    canCreateSupplier() {
      return can("purchase.supplier", "W");
    },
    formattedsuppliers() {
      return this.suppliers.map(supplier => ({ id: supplier.id, text: supplier.name }));
    },

    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    formattedAttributes() {
      return this.attributes.map(item => ({
        id: item.id,
        text: item.name
      }));
    },

    formattedAttributeValues() {
      return this.selected_attribute_values.map(item => ({
        id: item.id,
        text: item.value
      }));
    },

    isBank() {
      const val = this.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },

    totalAmount() {
      return this.addedItems
        .reduce((sum, item) => {
          const qty = parseFloat(item.quantity) || 0;
          const price = parseFloat(item.costing_unit_price) || 0;

          return sum + (qty * price);
        }, 0).toFixed(2);
    },

    // Due amount = Total - Paid
    dueAmount() {
      const total = parseFloat(this.totalAmount) || 0;
      const due = total;
      return (due < 0 ? 0 : due).toFixed(2);
    },

    isMultiVariant() {
      return this.addedItems.some(
        item => item.item_type_code === 'MULTI_VARIANT' || (
          item.item_type_code === 'WARRANTY' && (item.variant_id || item.variant_attributes?.length)
        )
      );
    },

    hasWarrantyItems() {
      return this.addedItems.some(item => item.item_type_code === 'WARRANTY');
    },

    gridColCount() {
      // #, item, variant, stock, batch, lot, mfg, expiry, [qty|warranty+serial], purchase, sell, action
      return this.hasWarrantyItems ? 13 : 12;
    },

    isSelectedWarranty() {
      return this.selectedItem?.item_type_code === 'WARRANTY';
    },

    selectedVariantLabel() {
      const attrs = this.selectedItem?.variant_attributes;
      if (!attrs?.length) return '';
      return attrs.map(v => `${v.attribute}: ${v.value}`).join(' | ');
    },

    currentInvoiceQuantity() {
      return this.addedItems.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0),
        0
      );
    },

    supplierInfoTitle() {
      if (!this.selectedSupplier) return '';
      const name = this.selectedSupplier.name || '';
      const phone = this.selectedSupplier.phone_number || '—';
      const due = this.selectedSupplier.due_balance || 0;
      return `${name} · ${phone} · Due ৳${due}`;
    },

    showAddVariantButton() {
      return this.selectedParentItem !== null;
    },
  },

  methods: {
      formatMoney(amount) {
        return truncateMoney2(amount);
      },

      async fetchDropdownData() {
        try {
          const [
            itemRes, warehouseRes, supplierRes, cash_typeRes, receive_noRes, bank_accRes, attributeRes
          ] = await Promise.all([
            this.$axios.get(apiUrls.item.get),
            this.$axios.get(apiUrls.inventory_warehouse.get),
            this.$axios.get(apiUrls.inventory_supplier.get),
            this.$axios.get(apiUrls.batch_cash_type.get),
            this.$axios.get(apiUrls.batch_receive_no.get),
            this.$axios.get(apiUrls.batch_bank_account.get),
            this.$axios.get(apiUrls.attribute.get)
          ]);

          this.items = itemRes?.data || [];
          this.warehouses = warehouseRes?.data || [];
          this.suppliers = supplierRes?.data || [];
          this.cash_types = cash_typeRes?.data || [];
          this.batches.receiving_no = receive_noRes?.data?.receiving_no || '';
          this.bank_accounts = bank_accRes?.data || [];
          this.attributes = attributeRes?.data || [];

          if (this.cash_types.length > 0 && !this.cash_type) {
            this.cash_type = this.cash_types[0].id;
          }

          if (this.bank_accounts.length > 0 && !this.bank_account) {
            this.bank_account = this.bank_accounts[0].id;
          }

        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      },

      onSearchInput() {
        if (this.warrantyEntryLocked) return;
        if (!String(this.query || '').trim()) {
          this.results = [];
          this.itemIndex = 0;
          this.selectedParentItem = null;
          return;
        }
        this.debouncedSearch();
      },

      async searchItems(options = {}) {
        if (this.warrantyEntryLocked) {
          this.results = [];
          return;
        }
        const listAll = Boolean(options && options.listAll);
        const q = String(this.query || '').trim();
        if (!listAll && !q) {
          this.results = [];
          this.selectedParentItem = null;
          return;
        }

        try {
          const response = await this.$axios.get(apiUrls.batch_item_search.get, {
            params: { q: listAll ? '' : q },
          });
          const results = response.data.results || [];
          this.results = results;
          this.itemIndex = 0;

          if (
            !listAll
            && results.length === 1
            && results[0].barcode_qr_code
            && results[0].barcode_qr_code === q
          ) {
            await this.selectItem(results[0]);
            return;
          }

          await this.$nextTick(() => {
            if (window.feather) feather.replace();
          });
        } catch (error) {
          console.error('Search error:', error);
          this.results = [];
          this.toast.error(apiErrorMessage(error) || 'Item search failed.');
        }
      },

      getAvailableStock(item) {
        if (!item) return 0;

        // Variant
        if (item.variant_id) {
          return Number(item.current_stock ?? 0);
        }

        // Normal item
        return Number(
          item.stock_quantity ??
          item.stock_qty ??
          item.current_stock ??
          0
        );
      },

      // Same rule as /items: MULTI_VARIANT, or any item that already has variants (e.g. WARRANTY).
      showsVariantDetails(item) {
        return item?.item_type_code === 'MULTI_VARIANT'
          || item?.item_type_code === 'WARRANTY'
          || !!item?.has_variants
          || !!item?.variant_id
          || !!(item?.variant_attributes?.length);
      },

      isVariantParent(item) {
        if (item?.variant_id || item?.variant_attributes) {
          return false;
        }
        // Same as /items showVariantDetails for parents: multi-variant type or any item with variants
        return item?.item_type_code === 'MULTI_VARIANT' || !!item?.has_variants;
      },

      formatSelectedItemQuery(item) {
        const name = item.item_name || '';
        const code = item.item_code ? ` - ${item.item_code}` : '';
        const attrs = item.variant_attributes?.length
          ? ` | ${item.variant_attributes.map(v => `${v.attribute}: ${v.value}`).join(' | ')}`
          : '';
        return `${name}${code}${attrs}`;
      },

      selectItem(item) {
        if (this.warrantyEntryLocked) return;

        const itemType = item.item_type_code
          || this.selectedParentItem?.item_type_code
          || null;
        if (itemType && !this.assertItemTypeAllowed(itemType)) {
          this.results = [];
          return;
        }

        // Match /items: open variant picker for multi-variant / warranty-with-variants parents
        if (this.isVariantParent(item)) {
          this.selectedParentItem = item;
          this.loadVariants(item.id || item.item_id)
          return
        }

        // Zero-stock items are allowed on purchase/receive
        this.selectedItem = item
        this.query = this.formatSelectedItemQuery(item)
        this.batches.item = item.item_id || item.id
        this.batches.variant_id = item.variant_id || null
        this.batches.costing_unit_price = item.costing_unit_price ?? item.purchase_price ?? ''
        this.batches.batch_selling_price = item.selling_price ?? item.batch_selling_price ?? ''
        if (item.item_type_code === 'WARRANTY') {
          this.batches.warranty_period_days = item.warranty_period_days || this.batches.warranty_period_days
          this.batches.serial_numbers_text = ''
          this.batches.quantity = null
          this.$nextTick(() => this.$refs.serialInput?.focus())
        } else {
          this.batches.warranty_period_days = null
          this.batches.serial_numbers_text = ''
        }
        this.results = []
        this.selectedParentItem = null
      },

      resetWarrantyLine() {
        // Unlock line fields for a new item search; keep grid rows as-is.
        // Next item starts a new batch line (does not append to previous lines).
        this.warrantyEntryLocked = false;
        this.activeWarrantyLineIndex = -1;
        this.query = '';
        this.results = [];
        this.itemIndex = 0;
        this.selectedItem = null;
        this.selectedParentItem = null;
        this.batches.item = null;
        this.batches.variant_id = null;
        this.batches.batch_no = '';
        this.batches.lot_no = '';
        this.batches.manufacturing_date = null;
        this.batches.expiry_date = null;
        this.batches.costing_unit_price = '';
        this.batches.batch_selling_price = '';
        this.batches.quantity = null;
        this.batches.warranty_period_days = null;
        this.batches.serial_numbers_text = '';
        this.errors = {};
        this.$nextTick(() => this.$refs.itemSearch?.focus());
      },

      async handleKeys(event) {
        if (this.warrantyEntryLocked) {
          event.preventDefault();
          return;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (!this.results.length) {
            await this.searchItems({ listAll: true });
            return;
          }
          this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
        } else if (event.key === 'ArrowUp') {
          this.itemIndex = Math.max(this.itemIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.itemIndex];
          if (selectedProduct) {
            this.selectItem(selectedProduct);
          }
          event.preventDefault();
        }
      },

      async loadVariants(itemId) {
        try {
          const response = await this.$axios.get(
            `/item/${itemId}/variants/`
          )

          const variants = response.data.results || [];
          this.results = variants;
          this.existingVariants = variants
          this.itemIndex = 0

          // Keep parent selected so Add Variant stays available when none exist yet
          if (!this.query && this.selectedParentItem) {
            this.query = this.selectedParentItem.item_name;
          }

        } catch (error) {
          console.error(error)
        }
      },

      fieldRequiredMsg(fieldKey) {
        return this.$t('error.field_required', { field: this.$t(fieldKey) });
      },

      assertPurchasePriceGtZero() {
        const price = Number(this.batches.costing_unit_price);
        if (
          this.batches.costing_unit_price === ''
          || this.batches.costing_unit_price === null
          || this.batches.costing_unit_price === undefined
          || !Number.isFinite(price)
          || price <= 0
        ) {
          this.toast.warning(
            this.$t('error.field_gt_zero', { field: this.$t('purchase_price') })
          );
          return false;
        }
        return true;
      },

      validateHeaderBeforeAdd() {
        this.errors = {};
        const requiredFields = [
          { key: 'chalan_no', label: 'chalan_no' },
          { key: 'chalan_date', label: 'chalan_date' },
          { key: 'supplier_id', label: 'supplier_name' },
          { key: 'in_house_date', label: 'in_house_date' },
          { key: 'warehouse_id', label: 'warehouse_name' },
          { key: 'receiving_date', label: 'receiving_date' },
        ];
        requiredFields.forEach(({ key, label }) => {
          if (!this.batches[key]) this.errors[key] = this.fieldRequiredMsg(label);
        });
        if (!this.selectedItem && !this.batches.item) {
          this.errors.item = this.fieldRequiredMsg('search_item_by_name_code');
        }
        if (Object.keys(this.errors).length > 0) {
          const messages = [...new Set(Object.values(this.errors).filter(Boolean))];
          this.toast.warning(messages.join('\n'), {
            bodyClassName: 'toast-multiline',
          });
          return false;
        }
        return true;
      },

      formatVariantAttrs(item) {
        const attrs = item?.variant_attributes;
        if (!attrs?.length) return '';
        return attrs.map((a) => `${a.attribute}: ${a.value}`).join(' | ');
      },

      assertItemTypeAllowed(currentItemType) {
        if (!this.addedItems.length) return true;
        const gridIsWarranty = this.addedItems.some(
          (item) => item.item_type_code === 'WARRANTY'
        );
        const currentIsWarranty = currentItemType === 'WARRANTY';
        // NO_VARIANT + MULTI_VARIANT may share one grid; WARRANTY must be alone.
        if (gridIsWarranty === currentIsWarranty) return true;
        if (currentIsWarranty) {
          this.toast.warning(this.$t('warranty_cannot_mix_with_other_types'));
        } else {
          this.toast.warning(this.$t('non_warranty_cannot_add_to_warranty_grid'));
        }
        return false;
      },

      warrantySerialsDisplay(item) {
        if (!item || item.item_type_code !== 'WARRANTY') return '';
        // One serial per line inside the Serial numbers cell for this batch.
        return this.parseSerialNumbers(item.serial_numbers_text).join('\n');
      },

      normalizeWarrantyReceiveLines(rows) {
        return rows.map((row) => {
          if (row.item_type_code !== 'WARRANTY') return row;
          const serials = this.parseSerialNumbers(row.serial_numbers_text);
          return {
            ...row,
            serial_numbers_text: serials.join('\n'),
            quantity: serials.length,
          };
        });
      },

      async serialExistsInDatabase(serial) {
        try {
          const { data } = await this.$axios.get(apiUrls.serialized_units.get, {
            params: { serial_number: serial },
          });
          return Boolean(data?.exists);
        } catch (error) {
          // Fail closed only for clear existence; otherwise let submit re-check.
          console.error('Serial existence check failed:', error);
          return false;
        }
      },

      async addWarrantySerial() {
        if (!this.selectedItem || this.selectedItem.item_type_code !== 'WARRANTY') {
          this.toast.warning("Please select a warranty item first.");
          return;
        }
        if (!this.validateHeaderBeforeAdd()) return;
        if (!this.assertItemTypeAllowed('WARRANTY')) return;
        if (!this.assertPurchasePriceGtZero()) return;

        if (!this.batches.warranty_period_days || Number(this.batches.warranty_period_days) <= 0) {
          this.toast.warning(this.fieldRequiredMsg('warranty_period_days'));
          return;
        }

        const serial = String(this.batches.serial_numbers_text || '').trim();
        if (!serial) {
          this.toast.warning(this.fieldRequiredMsg('serial_number'));
          return;
        }

        const serialKey = serial.toLowerCase();
        const duplicateInGrid = this.addedItems.some((row) => {
          if (row.item_type_code !== 'WARRANTY') return false;
          return this.parseSerialNumbers(row.serial_numbers_text)
            .some(s => s.toLowerCase() === serialKey);
        });
        if (duplicateInGrid) {
          this.toast.warning(
            this.$t('serial_number_already_exist')
            || 'Serial Number already exist'
          );
          this.batches.serial_numbers_text = '';
          this.$nextTick(() => this.$refs.serialInput?.focus());
          return;
        }

        if (await this.serialExistsInDatabase(serial)) {
          this.toast.warning(
            this.$t('serial_number_already_exist')
            || 'Serial Number already exist'
          );
          this.batches.serial_numbers_text = '';
          this.$nextTick(() => this.$refs.serialInput?.focus());
          return;
        }

        const itemId = this.selectedItem.item_id || this.selectedItem.id;
        const canAppendToActive =
          this.warrantyEntryLocked
          && this.activeWarrantyLineIndex >= 0
          && this.activeWarrantyLineIndex < this.addedItems.length
          && this.addedItems[this.activeWarrantyLineIndex]?.item_type_code === 'WARRANTY';

        if (canAppendToActive) {
          // Same item entry session: append serial on a new line inside this batch row.
          const existing = this.addedItems[this.activeWarrantyLineIndex];
          const serials = [...this.parseSerialNumbers(existing.serial_numbers_text), serial];
          existing.serial_numbers_text = serials.join('\n');
          existing.quantity = serials.length;
        } else {
          // New item (after Reset or first scan): new item line = new batch.
          this.addedItems.push({
            ...this.batches,
            item: itemId,
            item_id: itemId,
            variant_id: this.selectedItem.variant_id || null,
            item_name: this.selectedItem.item_name,
            item_code: this.selectedItem.item_code,
            item_type_code: 'WARRANTY',
            current_stock: this.getAvailableStock(this.selectedItem),
            costing_unit_price: this.batches.costing_unit_price || 0,
            batch_selling_price: this.batches.batch_selling_price || 0,
            selling_price: this.batches.batch_selling_price || 0,
            variant_attributes: this.selectedItem.variant_attributes || [],
            quantity: 1,
            batch_no: this.batches.batch_no,
            lot_no: this.batches.lot_no,
            manufacturing_date: this.batches.manufacturing_date,
            expiry_date: this.batches.expiry_date,
            warranty_period_days: this.batches.warranty_period_days,
            serial_numbers_text: serial,
          });
          this.activeWarrantyLineIndex = this.addedItems.length - 1;
        }

        // Lock item/search + line fields; only serial stays active for more units.
        this.warrantyEntryLocked = true;
        this.results = [];
        this.selectedParentItem = null;
        this.batches.serial_numbers_text = '';
        this.$nextTick(() => this.$refs.serialInput?.focus());
      },

      addItem() {
        if (!this.selectedItem) {
          this.toast.warning("Please select an item first.");
          return;
        }

        if (this.selectedItem.item_type_code === 'WARRANTY') {
          this.addWarrantySerial();
          return;
        }

        if (!this.validateHeaderBeforeAdd()) return;

        const qty = Number(this.batches.quantity);
        if (!qty || qty <= 0) {
          this.toast.warning(this.$t('error.field_gt_zero', { field: this.$t('quantity') }));
          return;
        }

        if (!this.assertPurchasePriceGtZero()) return;

        const currentItemId = this.selectedItem.item_id || this.selectedItem.id;
        const currentVariantId = this.selectedItem.variant_id || null;
        const currentItemType = this.selectedItem.item_type_code;

        if (!this.assertItemTypeAllowed(currentItemType)) {
          this.query = "";
          this.results = [];
          this.selectedItem = null;
          this.itemIndex = 0;
          return;
        }

        const alreadyExists = this.addedItems.some(item => {
            const itemId = item.item_id || item.item;
            const variantId = item.variant_id || null;

            if (currentVariantId) {
                return variantId === currentVariantId;
            }

            return itemId === currentItemId && !variantId;
        });

        if (alreadyExists) {
            this.toast.warning("This item/variant has already been added.");

            this.query = "";
            this.results = [];
            this.selectedItem = null;
            this.itemIndex = 0;
            return;
        }

        const newItem = {
          ...this.batches,

          item: this.selectedItem.item_id || this.selectedItem.id,
          item_id: this.selectedItem.item_id || this.selectedItem.id,
          variant_id: this.selectedItem.variant_id || null,
          item_name: this.selectedItem.item_name,
          item_code: this.selectedItem.item_code,
          item_type_code: this.selectedItem.item_type_code,
          current_stock: this.getAvailableStock(this.selectedItem),
          costing_unit_price: this.batches.costing_unit_price || 0,
          batch_selling_price: this.batches.batch_selling_price || 0,
          selling_price: this.batches.batch_selling_price || 0,
          variant_attributes:
            this.selectedItem.variant_attributes || [],

          quantity: qty,
          batch_no: this.batches.batch_no,
          lot_no: this.batches.lot_no,
          manufacturing_date: this.batches.manufacturing_date,
          expiry_date: this.batches.expiry_date,
          warranty_period_days: null,
          serial_numbers_text: '',
        };

        this.addedItems.push(newItem);

        // Reset only row fields, not header / voucher fields
        this.batches = {
          item: null, chalan_no: this.batches.chalan_no, chalan_date: this.batches.chalan_date,
          supplier_id: this.batches.supplier_id, batch_no: '', lot_no: '',
          costing_unit_price: '', batch_selling_price: '', quantity: null,
          in_house_date: this.batches.in_house_date,
          manufacturing_date: '', expiry_date: '',
          warehouse_id: this.batches.warehouse_id, receiving_no: this.batches.receiving_no,
          receiving_date: this.batches.receiving_date,
          discount: this.batches.discount, selling_price: 0,
          variant_id: null, attribute: null, attribute_values: [], variants: [],
          warranty_period_days: null, serial_numbers_text: '',
        };
        this.errors = {};
        this.query = "";
        this.results = [];
        this.selectedItem = null;
        this.selectedParentItem = null;
        this.itemIndex = 0;

        this.$nextTick(() => {
          this.$refs.itemSearch?.focus();
        });
      },

      async fetchReceivingNo() {
        try {
          let res = await this.$axios.get(apiUrls.batch_receive_no.get, {
            params: { receiving_date: this.batches.receiving_date }
          });
          this.batches.receiving_no = res.data.receiving_no;
        } catch (err) {
          console.error(err);
        }
      },

      openDueModal() {
        return new Promise((resolve) => {
          this.dueModalResolver = resolve;
          this.showDueModal = true;
        });
      },

      confirmDueSale() {
        this.showDueModal = false;
        if (this.dueModalResolver) this.dueModalResolver(true);
      },

      cancelDueSale() {
        this.showDueModal = false;
        if (this.dueModalResolver) this.dueModalResolver(false);
      },

      openVariantModal() {
        if (this.warrantyEntryLocked) return;
        // Hide search result dropdown
        this.results = [];
        this.query = "";

        // Reset modal state
        this.variant_groups = [];
        this.selected_attribute_values = [];
        this.batches.attribute = null;
        this.batches.attribute_values = [];

        this.generatedVariants = {
            existing: [],
            new: []
        };

        this.showVariantModal = true;
      },

      closeVariantModal() {
        if (this.showVariantSuccessAlert || this.savingVariants) return;

        // Hide search result dropdown
        this.results = [];
        this.query = "";

        // Reset parent item
        this.selectedParentItem = null;

        // Reset modal state
        this.variant_groups = [];
        this.selected_attribute_values = [];
        this.batches.attribute = null;
        this.batches.attribute_values = [];

        this.generatedVariants = {
            existing: [],
            new: []
        };

        this.showVariantModal = false;
      },

      async saveVariant() {
        if (this.savingVariants) return;
        if (!this.generatedVariants.new.length) {
          this.toast.warning("Please generate variants first.");
          return;
        }
        if (!this.selectedParentItem?.id) {
          this.toast.warning("Please select a parent item first.");
          return;
        }

        const parentType = this.selectedParentItem?.item_type_code;
        if (!this.assertItemTypeAllowed(parentType)) return;

        const parent = this.selectedParentItem;
        const purchasePrice = this.batches.costing_unit_price !== '' && this.batches.costing_unit_price != null
          ? this.batches.costing_unit_price
          : (parent.purchase_price ?? parent.costing_unit_price ?? 0);
        const sellingPrice = this.batches.batch_selling_price !== '' && this.batches.batch_selling_price != null
          ? this.batches.batch_selling_price
          : (parent.selling_price ?? 0);

        const variants = this.generatedVariants.new.map((variant) => ({
          sku: variant.sku,
          barcode_qr_code: variant.barcode,
          purchase_price: purchasePrice || 0,
          selling_price: sellingPrice || 0,
          discount: 0,
          attributes: (variant.attributes || []).map((attr) => ({
            attribute_id: attr.attribute_id,
            attribute_value_id: attr.attribute_value_id,
          })),
        }));

        this.savingVariants = true;
        try {
          await this.$axios.post(`/item/${parent.id}/variants/`, { variants });
          // Keep variant modal under the alert until OK; then close + focus search.
          this.showVariantSuccessAlert = true;
        } catch (error) {
          this.toast.error(apiErrorMessage(error));
        } finally {
          this.savingVariants = false;
        }
      },

      acknowledgeVariantSuccess() {
        this.showVariantSuccessAlert = false;
        this.showVariantModal = false;
        this.generatedVariants = { existing: [], new: [] };
        this.batches.variants = [];
        this.variant_groups = [];
        this.selected_attribute_values = [];
        this.batches.attribute = null;
        this.batches.attribute_values = [];
        this.query = '';
        this.results = [];
        this.selectedParentItem = null;
        this.existingVariants = [];
        this.itemIndex = 0;
        this.$nextTick(() => this.$refs.itemSearch?.focus());
      },

      handleAttributeSelect(selected) {
        this.batches.attribute = Number(selected.id);

        this.batches.attribute_values = [];
        this.batches.variants = [];

        const attribute = this.attributes.find(
          x => Number(x.id) === Number(selected.id)
        );

        this.selected_attribute_values = (attribute?.values || []).map(v => ({
          id: Number(v.id),
          value: v.value
        }));
      },

      showAttributeModal(id) {
        const el = document.getElementById(id);
        if (!el) return;
        window.bootstrap.Modal.getOrCreateInstance(el).show();
      },

      openAttributeValueModal() {
        if (!this.batches.attribute) {
          this.toast.error("Please select an attribute first");
          return;
        }
        this.showAttributeModal('create-modal9');
      },

      onAttributeCreated(newAttr) {
        if (!newAttr?.id) return;
        if (!this.attributes.some(a => Number(a.id) === Number(newAttr.id))) {
          this.attributes.push({ ...newAttr, values: newAttr.values || [] });
        }
        this.handleAttributeSelect(newAttr);
      },

      async onAttributeValueCreated(payload) {
        const attributeId = Number(payload?.attributeId || this.batches.attribute);
        if (!attributeId) return;
        try {
          const res = await this.$axios.get(apiUrls.attribute.get);
          this.attributes = res.data || [];
        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          return;
        }
        const attribute = this.attributes.find(a => Number(a.id) === attributeId);
        if (!attribute || Number(this.batches.attribute) !== attributeId) return;

        this.selected_attribute_values = (attribute.values || []).map(v => ({
          id: Number(v.id),
          value: v.value
        }));
        const created = (payload?.created || []).map(n => String(n).toLowerCase());
        const newIds = this.selected_attribute_values
          .filter(v => created.includes(String(v.value).toLowerCase()))
          .map(v => v.id);
        const current = (this.batches.attribute_values || []).map(Number);
        this.batches.attribute_values = [...new Set([...current, ...newIds])];
      },

      addVariantGroup() {
        const attribute = this.attributes.find(
            x => Number(x.id) === Number(this.batches.attribute)
        );

        if (!attribute || !this.batches.attribute_values.length) {
            return;
        }

        const selectedIds = this.batches.attribute_values.map(Number);

        const values = this.selected_attribute_values.filter(v =>
            selectedIds.includes(Number(v.id))
        );

        // Check if this attribute group already exists
        const existingGroup = this.variant_groups.find(
            g => Number(g.attribute_id) === Number(attribute.id)
        );

        if (existingGroup) {
            // Add only new values
            values.forEach(value => {
                const exists = existingGroup.values.some(
                    v => Number(v.id) === Number(value.id)
                );

                if (!exists) {
                    existingGroup.values.push({
                        id: value.id,
                        value: value.value
                    });
                }
            });

            this.toast.success("Attribute values updated.");
        } else {
            // Create new attribute group
            this.variant_groups.push({
                attribute_id: attribute.id,
                attribute_name: attribute.name,
                values: values.map(v => ({
                    id: v.id,
                    value: v.value
                }))
            });

            this.toast.success("Attribute group added.");
        }

        // Reset selection
        this.batches.attribute = null;
        this.batches.attribute_values = [];
        this.selected_attribute_values = [];
    },

      generateVariants() {
        if (!this.variant_groups.length) {
          return;
        }

        let combinations = [[]];

        this.variant_groups.forEach(group => {

          const temp = [];
          group.values.forEach(val => {
            combinations.forEach(existing => {
              temp.push([
                ...existing,
                {
                  attribute_id: group.attribute_id,
                  attribute: group.attribute_name,
                  attribute_value_id: val.id,
                  value: val.value,
                }
              ]);
            });
          });

          combinations = temp;
        });

        const allExisting = [
          ...(this.existingVariants || []),
          ...this.addedItems.filter(
            x => x.is_new_variant && Number(x.item) === Number(this.selectedParentItem.id)
          )
        ];

        const existingKeys = new Set(
          allExisting.map(v =>
            (v.variant_attributes || [])
              .map(a => `${a.attribute}:${a.value}`)
              .sort()
              .join("|")
          )
        );

        const existingVariants = [];
        const newVariants = [];

        const baseBarcode = this.selectedParentItem.barcode_qr_code || '00000';
        let counter = this.existingVariants.length + 1;

        combinations.forEach(combo => {
            const key = combo
                .map(a => `${a.attribute}:${a.value}`)
                .sort()
                .join('|');

            const variant = {
                item_name: this.selectedParentItem.item_name,
                barcode: `${baseBarcode}-${counter++}`,
                attributes: combo,
                sku: `${this.selectedParentItem.item_name}-${combo.map(c=>c.value).join('-')}`,
            };

            if (existingKeys.has(key)) {
                existingVariants.push(variant);
            } else {
                newVariants.push(variant);
            }
        });

        // Nothing new was generated
        if (newVariants.length === 0) {
          this.generatedVariants = {
              existing: existingVariants,
              new: []
          };

          this.toast.warning("All selected variant combinations already exist.");
          return;
        }

        this.generatedVariants = {
            existing: existingVariants,
            new: newVariants
        };

        // Optional success message
        this.toast.success(`${newVariants.length} new variant(s) generated.`);
      },

      parseSerialNumbers(text) {
        return String(text || '')
          .replace(/,/g, '\n')
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean);
      },

      async submitBatch() {
        if (this.saving) return;
        if (!this.addedItems.length) {
          this.toast.warning("Please add at least one batch before submitting.");
          return;
        }

        // One InvBatchDetail per warranty item line (serials newline-joined on that batch).
        const submitRows = this.normalizeWarrantyReceiveLines(this.addedItems);
        this.addedItems = submitRows;

        for (const item of submitRows) {
          if (item.item_type_code !== 'WARRANTY') continue;
          const serials = this.parseSerialNumbers(item.serial_numbers_text);
          if (!item.warranty_period_days || Number(item.warranty_period_days) <= 0) {
            this.toast.warning(this.$t('warranty_period_required') || 'Warranty period is required.');
            return;
          }
          if (!serials.length) {
            this.toast.warning(
              this.$t('serial_count_mismatch') ||
              `Provide serial number(s) for ${item.item_name}.`
            );
            return;
          }
        }

        const newVariants = [];

        const batches = submitRows.map(item => {

          if (item.is_new_variant) {
            newVariants.push({
              temp_barcode: item.barcode_qr_code,
              item: item.item,
              sku: item.sku,
              barcode_qr_code: item.barcode_qr_code,
              quantity: item.quantity,
              purchase_price: item.costing_unit_price,
              selling_price: item.batch_selling_price,
              discount: item.discount,

              attributes: item.variant_attributes.map(attr => ({
                attribute_id: attr.attribute_id,
                attribute_value_id: attr.attribute_value_id
              }))
            });
          }

          const row = {
            item: item.item,
            // existing variant id only
            variant_id: item.is_new_variant ? null : item.variant_id,
            // backend will use this to match after variant creation
            temp_barcode: item.is_new_variant
              ? item.barcode_qr_code
              : null,

            chalan_no: item.chalan_no,
            chalan_date: item.chalan_date,
            supplier_id: item.supplier_id,
            warehouse_id: item.warehouse_id,
            receiving_no: this.batches.receiving_no || item.receiving_no,
            receiving_date: this.batches.receiving_date || item.receiving_date,
            in_house_date: item.in_house_date,
            batch_no: item.batch_no,
            lot_no: item.lot_no,
            manufacturing_date: item.manufacturing_date || null,
            expiry_date: item.expiry_date || null,
            quantity: item.quantity,
            costing_unit_price: item.costing_unit_price,
            selling_price: item.batch_selling_price,
            discount: item.discount,
          };
          if (item.item_type_code === 'WARRANTY') {
            row.warranty_period_days = item.warranty_period_days;
            row.serial_numbers = this.parseSerialNumbers(item.serial_numbers_text);
          }
          return row;
        });

        this.saving = true;
        try {
          const payload = {
            batches: batches,
            new_variants: newVariants,
            payment: {
              due: this.dueAmount,
              reference_no: this.reference_no,
            }
          };

          await this.$axios.post(apiUrls.inv_batch.post, payload );

          this.toast.success("Batches submitted successfully!");
          this.$router.push('/batches');
          this.$emit("create_success");
          this.resetForm();
          this.addedItems = [];
        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          }
        } finally {
          this.saving = false;
        }
      },

      onSupplierCreated(newSupplier) {
        if (!newSupplier?.id) return;
        if (!this.suppliers.some((s) => Number(s.id) === Number(newSupplier.id))) {
          this.suppliers.push(newSupplier);
        }
        this.batches.supplier_id = newSupplier.id;
        delete this.errors.supplier_id;
        this.selectSupplier(newSupplier.id);
      },

      async selectSupplier(value) {
        // dropdown gives only the supplier id
        const supplierId = value;

        if (!supplierId) {
          this.selectedSupplier = null;
          this.due_balance = 0;
          return;
        }

        // get full object from loaded array
        const supplierObj = this.formattedsuppliers.find((s) => s.id === supplierId);

        try {
          const response = await this.$axios.get(`/supplier/due/${supplierId}`);
          this.selectedSupplier = {
            ...supplierObj,
            due_balance: response.data.due_balance,
            name: response.data.supplier_name,
            phone_number: response.data.supplier_phone
          };
          this.due_balance = response.data.due_balance;
        } catch (error) {
          console.error("Error fetching due balance:", error);
          this.due_balance = 0;
        }
      },

      cancelForm() {
        this.resetForm();
        this.addedItems = [];
        this.activeWarrantyLineIndex = -1;
        this.reference_no = '';
        this.paid = 0;
        this.selectedSupplier = null;
        this.due_balance = 0;
        this.showDueModal = false;
        this.showVariantModal = false;
        this.showVariantSuccessAlert = false;
        this.savingVariants = false;
        this.selectedParentItem = null;
        this.results = [];
        this.itemIndex = 0;
        this.variant_groups = [];
        this.existingVariants = [];
        this.generatedVariants = { existing: [], new: [] };
        this.batches.attribute = null;
        this.batches.attribute_values = [];
        this.batches.variants = [];
        this.batches.variant_id = null;
        this.batches.chalan_date = todayLocal();
        this.batches.in_house_date = todayLocal();
        this.batches.receiving_date = todayLocal();
        this.fetchReceivingNo();
      },

      removeWarrantySerial(rowIndex, serialIndex) {
        const row = this.addedItems[rowIndex];
        if (!row || row.item_type_code !== 'WARRANTY') return;
        const serials = this.parseSerialNumbers(row.serial_numbers_text);
        if (serialIndex < 0 || serialIndex >= serials.length) return;
        serials.splice(serialIndex, 1);
        if (!serials.length) {
          this.removeItem(rowIndex);
          return;
        }
        row.serial_numbers_text = serials.join('\n');
        row.quantity = serials.length;
      },

      removeItem(index) {
        this.addedItems.splice(index, 1);
        if (this.activeWarrantyLineIndex === index) {
          this.activeWarrantyLineIndex = -1;
          this.warrantyEntryLocked = false;
        } else if (this.activeWarrantyLineIndex > index) {
          this.activeWarrantyLineIndex -= 1;
        }
      },

      removeVariantGroup(index) {
        this.variant_groups.splice(index, 1);

        this.generatedVariants.new = [];
        this.batches.variants = [];
      },

      clearError(field) {
        if (this.errors[field]) {
          delete this.errors[field];
        }
      },

      handleSelect2Change(field, value) {
        // clear error
        delete this.errors[field];

        // set selected value
        this.items[field] = value.id;
      },

      resetForm() {
        // Reset form fields and errors
        this.batches = {
          item: '', chalan_no: '', chalan_date: '', supplier_id: '', batch_no: '', lot_no: '',
          costing_unit_price: '', batch_selling_price: '', quantity: '',
          in_house_date: '', manufacturing_date: '', expiry_date: '', warehouse_id: '',
          receiving_no: '', receiving_date: '',
          warranty_period_days: null, serial_numbers_text: '',
        };
        this.errors = {};
        this.errorMessage = "";
        this.selectedItem = null;
        this.warrantyEntryLocked = false;
        this.activeWarrantyLineIndex = -1;
        this.query = '';
        this.cash_type = this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '';
        this.bank_account = null;
      }
    },

  watch: {
    'batches.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.batches.receiving_no = '';
      }
    },

    'cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.bank_account = null;
      }
    },

    isBank(newVal) {
      if (!newVal) this.bank_account = null;
    },
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },
};
</script>


<style scoped>
.batch-create-page-header {
  margin-bottom: 8px !important;
  min-height: 0;
  padding: 0;
  gap: 8px;
  align-items: center;
}

.batch-create-page-header .page-title h4 {
  margin-bottom: 0;
  font-size: 18px;
  line-height: 1.2;
}

.batch-create-header-actions {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  min-height: 0 !important;
  gap: 10px !important;
}

.batch-create-header-actions .page-btn {
  margin-left: 0;
  margin-top: 0 !important;
}

.batch-header-invoice-qty {
  list-style: none;
  display: flex;
  align-items: center;
}

.batch-create-card {
  padding: 12px !important;
}

.batch-create-card .form-label {
  margin-bottom: 4px;
}

.supplier-info-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  margin-top: 28px;
  padding: 6px 10px;
  overflow: hidden;
  background: #fff8e6;
  border: 1px solid #f0d48a;
  border-radius: 8px;
  box-sizing: border-box;
}

.supplier-info-panel.is-empty {
  visibility: hidden;
  pointer-events: none;
}

.supplier-info-main {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.supplier-info-name,
.supplier-info-phone {
  font-size: 12px;
  font-weight: 600;
  color: #5c4a1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.supplier-info-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 55%;
}

.supplier-info-phone {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 40%;
  font-weight: 500;
  color: #9a6700;
}

.supplier-info-sep {
  flex: 0 0 auto;
  color: #f0d48a;
  font-size: 12px;
  line-height: 1;
}

.supplier-info-due {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.35;
  white-space: nowrap;
  border: 1px solid transparent;
  background: #ffffff;
}

.supplier-info-due.is-due {
  border-color: #f1aeb5;
  background: #f8d7da;
  color: #dc3545;
}

.supplier-info-due.is-clear {
  border-color: #a3cfbb;
  background: #d1e7dd;
  color: #198754;
}

.batch-line-stock-bar {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  max-width: 280px;
  padding: 6px 10px 6px 12px;
  background: linear-gradient(135deg, #edf9f1 0%, #e3f6ea 100%);
  border: 1px solid #b7e4c7;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(25, 135, 84, 0.08);
}

.batch-line-invoice-qty-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #198754;
  white-space: nowrap;
}

.batch-line-invoice-qty-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: #198754;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.batch-line-entry {
  background: #f3f6fa;
  border: 1px solid #e4ebf3;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 0;
  min-height: 78px;
  box-sizing: border-box;
}

.batch-line-entry input[type='number']::-webkit-outer-spin-button,
.batch-line-entry input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.batch-line-entry input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.batch-line-fields {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 6px;
}

.batch-line-field {
  flex: 1 1 0;
  min-width: 0;
}

.batch-line-field .form-label {
  display: block;
  margin-bottom: 2px;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.batch-line-field .form-control {
  min-width: 0;
  padding-left: 6px;
  padding-right: 6px;
  font-size: 12px;
}

.batch-line-search {
  flex: 0.9 1 80px;
  max-width: 160px;
}

.batch-line-search.batch-line-search-wide {
  flex: 1.6 1 180px;
  max-width: 280px;
}

.batch-line-serials {
  flex: 1.4 1 140px;
  min-width: 140px;
}

.batch-line-serials-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.batch-line-serials-row .form-control {
  flex: 1 1 auto;
  min-width: 0;
}

.batch-line-reset {
  flex: 0 0 auto;
  height: 31px;
  min-height: 31px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.batch-line-date {
  flex: 0.48 1 100px;
  max-width: 120px;
}

.batch-line-qty {
  flex: 0.55 1 56px;
  max-width: 72px;
}

.batch-line-warranty {
  flex: 0.7 1 72px;
  max-width: 96px;
}

.batch-line-add {
  flex: 0 0 68px;
  max-width: 68px;
}

.batch-line-add .btn-success {
  height: 31px;
  min-height: 31px;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 31px;
  font-weight: 600;
}

.batch-line-entry .form-control-sm {
  height: 31px;
  min-height: 31px;
}

.batch-create-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.batch-create-ref {
  display: flex;
  flex-direction: column;
  width: 200px;
  max-width: 100%;
}

.batch-create-total {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.batch-create-total .form-label {
  width: 35px;
  margin-bottom: 0;
}

.batch-create-total .form-control {
  width: 140px;
}

.batch-create-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 1199.98px) {
  .batch-line-fields {
    flex-wrap: wrap;
  }

  .batch-line-field {
    flex: 1 1 calc(25% - 6px);
    min-width: 120px;
    max-width: none;
  }

  .batch-line-search,
  .batch-line-search.batch-line-search-wide {
    flex: 1 1 100%;
    max-width: none;
  }

  .batch-line-date,
  .batch-line-qty,
  .batch-line-warranty,
  .batch-line-serials {
    max-width: none;
  }

  .batch-line-add {
    flex: 0 0 auto;
    max-width: none;
    min-width: 80px;
  }
}

/* Mobile: stack line entry; keep grid readable via horizontal scroll */
@media (max-width: 767.98px) {
  .batch-create-page-header {
    flex-wrap: wrap;
    gap: 6px;
  }

  .batch-create-card {
    padding: 10px !important;
  }

  .batch-line-stock-bar {
    max-width: 220px;
    padding: 5px 8px 5px 10px;
    gap: 6px;
  }

  .batch-line-invoice-qty-label {
    font-size: 11px;
  }

  .batch-line-invoice-qty-value {
    min-width: 24px;
    height: 22px;
    font-size: 12px;
  }

  .batch-line-entry {
    padding: 8px;
    min-height: 120px;
  }

  .batch-line-fields {
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
  }

  .batch-line-field {
    flex: 1 1 calc(50% - 8px);
    min-width: calc(50% - 8px);
    max-width: none;
  }

  .batch-line-search,
  .batch-line-search.batch-line-search-wide,
  .batch-line-serials {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .batch-line-add {
    flex: 1 1 100%;
    min-width: 100%;
    max-width: none;
  }

  .batch-line-add .btn-success {
    width: 100%;
  }

  .batch-line-field .form-label {
    white-space: normal;
  }

  .batch-line-entry .position-relative .search-results {
    min-width: 0 !important;
    width: 100% !important;
    left: 0;
    right: 0;
  }

  .fixed-table.excel-grid-wrap {
    height: 220px;
    max-height: 220px;
    -webkit-overflow-scrolling: touch;
  }

  .excel-grid {
    width: 860px; /* preserve column layout; scroll horizontally on small screens */
    min-width: 860px;
  }

  .batch-create-footer-row {
    flex-direction: column;
    align-items: stretch;
  }

  .batch-create-ref {
    width: 100%;
  }

  .batch-create-total {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
  }

  .batch-create-total .form-control {
    flex: 1;
    width: auto;
  }

  .batch-create-actions {
    justify-content: stretch;
  }

  .batch-create-actions .btn {
    flex: 1 1 auto;
  }

  .batch-create-card .col-lg-3.col-md-6 .d-flex.align-items-start {
    flex-direction: column;
  }
}

@media (max-width: 575.98px) {
  .batch-line-field {
    flex: 1 1 100%;
    min-width: 100%;
  }
}

/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  height: 280px;
  max-height: 280px;
  overflow: auto;
  border: 1px solid #a6a6a6;
  border-radius: 0;
  background: #fff;
}

.excel-grid-wrap {
  padding: 0;
}

.excel-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11px;
  font-family: Calibri, "Segoe UI", Arial, sans-serif;
  color: #000;
  background: #fff;
}

/* Widths on <col> — required for table-layout:fixed to honor a 25px action column */
.excel-grid col.excel-col-hash { width: 3%; }
.excel-grid col.excel-col-item { width: 16%; }
.excel-grid col.excel-col-variant { width: 12%; }
.excel-grid col.excel-col-stock { width: 7%; }
.excel-grid col.excel-col-batch,
.excel-grid col.excel-col-lot { width: 7%; }
.excel-grid col.excel-col-date { width: 8%; }
.excel-grid col.excel-col-qty { width: 5%; }
.excel-grid .col-hash {
  text-align: center;
  font-weight: 600;
}
.excel-grid col.excel-col-money { width: 8%; }
.excel-grid col.excel-col-warranty { width: 6%; }
.excel-grid col.excel-col-serial { width: 9%; }
.excel-grid col.excel-col-action {
  width: 25px;
  min-width: 25px;
  max-width: 25px;
}

.excel-grid thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f2f2f2;
  border: 1px solid #b4b4b4;
  padding: 3px 5px;
  font-weight: 700;
  font-size: 11px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  vertical-align: middle;
  box-sizing: border-box;
}

.excel-grid tbody td {
  border: 1px solid #d0d0d0;
  padding: 2px 5px;
  height: 22px;
  line-height: 1.25;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #fff;
  box-sizing: border-box;
}

.excel-grid tbody tr:nth-child(even) td {
  background: #fafafa;
}

.excel-grid tbody tr:hover td {
  background: #e7f3ff;
}

.excel-grid .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.excel-grid .col-item,
.excel-grid .col-variant {
  text-align: left;
}

.excel-grid .col-date {
  text-align: center;
}

.excel-grid .warranty-serial-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
}

.excel-grid .warranty-serial-list {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  line-height: 1.35;
}

.excel-grid .warranty-serial-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.excel-grid .warranty-serial-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.excel-grid .warranty-serial-cancel {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  margin: 0;
  border: 1px solid #c45c5c;
  border-radius: 2px;
  background: #fff;
  color: #c45c5c;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
}

.excel-grid .warranty-serial-cancel:hover {
  background: #c45c5c;
  color: #fff;
}

.excel-grid .warranty-serial-count {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  margin-left: auto;
  padding: 0 5px;
  border-radius: 999px;
  background: #198754;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.excel-grid .col-serial {
  white-space: normal;
  vertical-align: top;
}

.excel-grid th.col-action,
.excel-grid td.col-action {
  width: 25px !important;
  min-width: 25px !important;
  max-width: 25px !important;
  padding: 0 !important;
  text-align: center;
  overflow: hidden;
  font-size: 9px;
  letter-spacing: -0.04em;
}

.excel-grid-del {
  border: 1px solid #c45c5c;
  background: #fff;
  color: #c45c5c;
  display: inline-block;
  width: 14px;
  height: 14px;
  padding: 0;
  margin: 0;
  font-size: 11px;
  line-height: 12px;
  border-radius: 2px;
  cursor: pointer;
  vertical-align: middle;
}

.excel-grid-del:hover {
  background: #c45c5c;
  color: #fff;
}

.excel-grid-empty td {
  height: 1px;
  border: 1px solid #d0d0d0;
  background: #fff !important;
  vertical-align: top;
}

/* Add Attribute / Attribute Value open from inside the variant modal (.confirm-backdrop). */
.over-variant-modal {
  z-index: 10050;
}

/* Success alert sits above the variant create modal */
.variant-success-alert {
  z-index: 10060;
}

/* Modal Backdrop */
.confirm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal Box */
.confirm-modal {
  width: 420px;
  background: #ffffff;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: fadeInScale 0.2s ease;
}

/* Header */
.confirm-header h4 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 600;
}

/* Body Rows */
.confirm-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.confirm-row.due {
  color: #dc3545;
  font-weight: 600;
  font-size: 16px;
}

/* Buttons */
.confirm-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

/* Animation */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.error-slot {
  min-height: 18px;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.2;
}

</style>

